/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'
import { injectIntl } from 'react-intl'
import { Formik } from 'formik'

import { EuiForm } from '@elastic/eui'

import type { ActiveDirectorySettings } from '@modules/cloud-api/v1/types'
import type { AsyncRequestState, RegionId } from '@modules/ui-types'
import history from '@modules/utils/history'
import Header from '@modules/cui/Header'

import ActiveDirectoryProviderForm from '../ActiveDirectoryProviderForm'
import validateActiveDirectoryForm from '../validateActiveDirectoryForm'
import { authenticationProvidersUrl } from '../../../../../../../lib/urlBuilder'
import { addActiveDirectoryAuthProviderCrumbs } from '../../../../../../../lib/crumbBuilder'
import { messages } from '../../authProviderMessages'
import { formToSettings, getInitialFormValues } from '../requestMapping'

import type { WrappedComponentProps } from 'react-intl'
import type { FunctionComponent } from 'react'

interface Props extends WrappedComponentProps {
  onSubmit: (payload: ActiveDirectorySettings) => Promise<any>
  submitRequest: AsyncRequestState
  regionId: RegionId
  isPlanChangePending?: boolean
  isSecurityDeploymentDisabled?: boolean
}

const AddActiveDirectoryProvider: FunctionComponent<Props> = ({
  intl,
  regionId,
  onSubmit,
  submitRequest,
  isPlanChangePending = false,
  isSecurityDeploymentDisabled = false,
}) => (
  <Fragment>
    <Header
      name={intl.formatMessage(messages.addActiveDirectoryProvider)}
      breadcrumbs={addActiveDirectoryAuthProviderCrumbs({ regionId })}
    />

    <EuiForm>
      <Formik
        initialValues={getInitialFormValues()}
        validate={validateActiveDirectoryForm(intl)}
        onSubmit={(values) =>
          onSubmit(formToSettings(values)).then(() =>
            history.push(authenticationProvidersUrl(regionId)),
          )
        }
      >
        {(props) => (
          <ActiveDirectoryProviderForm
            {...props}
            submitRequest={submitRequest}
            disabled={isPlanChangePending || isSecurityDeploymentDisabled}
            onCancel={() => history.push(authenticationProvidersUrl(regionId))}
            regionId={regionId}
          />
        )}
      </Formik>
    </EuiForm>
  </Fragment>
)

export default injectIntl(AddActiveDirectoryProvider)
